/**
 * Bundle Builder
 * Lets a shopper pick a fixed number of items from a collection grid,
 * tracks the running total from real variant prices, and adds every
 * selected item to the cart in one request when they hit the target count.
 *
 * Add-to-cart dispatches the theme's standard CartLinesUpdateEvent (the same
 * event product-form.js dispatches) instead of doing a raw fetch + hard
 * redirect to /cart. This lets <cart-drawer-component> (and any app script
 * such as Shiprocket/Fastrr that listens for the same standard event or
 * cart-drawer opening) pick it up and render its own cart UI, instead of
 * navigating away to the full cart page mid-flow.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

(function () {
  function initBundleBuilder(root) {
    if (root.dataset.bbInitialized === 'true') return;
    root.dataset.bbInitialized = 'true';

    var maxItems = parseInt(root.dataset.maxItems, 10) || 6;
    var currencySymbol = root.dataset.currencySymbol || '';
    var bundleVariantId = root.dataset.bundleVariantId || null;
    var bundlePriceCents = root.dataset.bundlePriceCents ? parseInt(root.dataset.bundlePriceCents, 10) : null;
    var bar = root.querySelector('[data-bb-bar]');
    var countEl = root.querySelector('[data-bb-count]');
    var totalEl = root.querySelector('[data-bb-total]');
    var submitBtn = root.querySelector('[data-bb-submit]');
    var errorEl = root.querySelector('[data-bb-error]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-bb-card]'));
    var slots = Array.prototype.slice.call(root.querySelectorAll('[data-bb-slot]'));
    var infoFill = root.querySelector('[data-bb-info-fill]');
    var infoCount = root.querySelector('[data-bb-info-count]');
    var infoPrice = root.querySelector('[data-bb-info-price]');
    var introPopup = root.querySelector('[data-bb-intro-popup]');
    var introClose = root.querySelector('[data-bb-intro-close]');
    var introCta = root.querySelector('[data-bb-intro-cta]');

    var selections = {}; // variantId -> { qty, priceCents, card }
    var order = []; // variantIds in the order they were picked, for slot placement

    function totalSelected() {
      var total = 0;
      for (var id in selections) {
        total += selections[id].qty;
      }
      return total;
    }

    function totalCents() {
      var total = 0;
      for (var id in selections) {
        total += selections[id].qty * selections[id].priceCents;
      }
      return total;
    }

    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      if (amount.endsWith('.00')) {
        amount = amount.slice(0, -3);
      }
      return currencySymbol + amount;
    }

    // Flatten selections into one slot per unit (so qty=2 of one product fills 2 slots)
    function buildSlotFill() {
      var fillList = [];
      order.forEach(function (variantId) {
        var entry = selections[variantId];
        if (!entry) return;
        for (var i = 0; i < entry.qty; i++) {
          fillList.push(variantId);
        }
      });
      return fillList;
    }

    function updateUI() {
      var selected = totalSelected();

      if (selected > 0) {
        bar.hidden = false;
      } else {
        bar.hidden = true;
      }

      if (countEl) countEl.textContent = selected;
      if (infoCount) infoCount.textContent = selected;

      var pct = Math.min((selected / maxItems) * 100, 100) + '%';
      if (infoFill) infoFill.style.width = pct;

      var isComplete = selected === maxItems;
      submitBtn.disabled = !isComplete;
      var displayCents = bundlePriceCents !== null ? bundlePriceCents : totalCents();
      totalEl.textContent = selected > 0 ? formatMoney(displayCents) : '';

      cards.forEach(function (card) {
        var variantId = card.dataset.variantId;
        var qty = (selections[variantId] && selections[variantId].qty) || 0;
        var qtyEl = card.querySelector('[data-bb-qty]');
        qtyEl.textContent = qty;
        card.classList.toggle('is-selected', qty > 0);

        var increaseBtn = card.querySelector('[data-bb-increase]');
        var decreaseBtn = card.querySelector('[data-bb-decrease]');
        var isSoldOut = card.classList.contains('bb-card--sold-out');
        var inventoryLimit = parseInt(card.dataset.inventoryLimit, 10);
        if (isNaN(inventoryLimit)) inventoryLimit = -1;
        var atStockLimit = inventoryLimit >= 0 && qty >= inventoryLimit;

        decreaseBtn.disabled = qty === 0;
        increaseBtn.disabled = isSoldOut || atStockLimit || selected >= maxItems;
      });

      // Fill visual slots with thumbnails of selected items, in pick order
      var fillList = buildSlotFill();
      slots.forEach(function (slot, index) {
        var variantId = fillList[index];
        if (variantId) {
          var entry = selections[variantId];
          var imageEl = entry.card.querySelector('.bb-card__image');
          var title = entry.card.dataset.title || '';
          slot.classList.add('is-filled');
          slot.style.backgroundImage = imageEl ? 'url(' + imageEl.src + ')' : '';
          slot.setAttribute('aria-label', 'Jump to ' + title + ' in the grid');
          slot.onclick = (function (card) {
            return function () {
              card.scrollIntoView({ behavior: 'smooth', block: 'center' });
              card.classList.add('bb-card--highlight');
              setTimeout(function () {
                card.classList.remove('bb-card--highlight');
              }, 1200);
            };
          })(entry.card);
        } else {
          slot.classList.remove('is-filled');
          slot.style.backgroundImage = '';
          slot.setAttribute('aria-label', 'Empty slot ' + (index + 1));
          slot.onclick = null;
        }
      });
    }

    function refreshCart() {
      var cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
        return customElements.whenDefined('cart-items-component').then(function () {
          return cartItemsComponent.fetchCartData();
        });
      }
      return fetch(Theme.routes.cart_url + '.json', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then(function (response) {
        if (!response.ok) throw new Error('Failed to fetch cart: ' + response.status);
        return response.json();
      });
    }

    function markSoldOut(variantId) {
      var entry = selections[variantId];
      delete selections[variantId];
      order = order.filter(function (id) { return id !== variantId; });

      var card = (entry && entry.card) || root.querySelector('[data-bb-card][data-variant-id="' + variantId + '"]');
      if (!card) return;

      card.classList.add('bb-card--sold-out');
      card.dataset.inventoryLimit = '0';

      var increaseBtn = card.querySelector('[data-bb-increase]');
      var decreaseBtn = card.querySelector('[data-bb-decrease]');
      if (increaseBtn) increaseBtn.disabled = true;
      if (decreaseBtn) decreaseBtn.disabled = true;

      if (!card.querySelector('.bb-card__sold-out-badge')) {
        var wrap = card.querySelector('.bb-card__image-wrap');
        if (wrap) {
          var badge = document.createElement('span');
          badge.className = 'bb-card__sold-out-badge';
          badge.textContent = 'Sold out';
          wrap.appendChild(badge);
        }
      }
    }

    // Attempt to figure out which currently-selected item Shopify's stock
    // check rejected, using the title Shopify includes in its error text.
    // /cart/add.js fails the whole request atomically when any one item is
    // out of stock, so nothing was actually added yet at this point — we
    // just need to fix up the offending pick so the shopper can swap it out.
    function findSoldOutVariantId(message) {
      if (!message) return null;
      var lowerMessage = message.toLowerCase();
      for (var id in selections) {
        var title = selections[id].card.dataset.title;
        if (title && lowerMessage.indexOf(title.toLowerCase()) !== -1) {
          return id;
        }
      }
      return null;
    }

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    cards.forEach(function (card) {
      var variantId = card.dataset.variantId;
      var priceCents = parseInt(card.dataset.priceCents, 10) || 0;
      var inventoryLimit = parseInt(card.dataset.inventoryLimit, 10);
      if (isNaN(inventoryLimit)) inventoryLimit = -1; // -1 = unlimited (not tracked / oversell allowed)
      var increaseBtn = card.querySelector('[data-bb-increase]');
      var decreaseBtn = card.querySelector('[data-bb-decrease]');

      increaseBtn.addEventListener('click', function () {
        clearError();
        if (totalSelected() >= maxItems) return;
        var currentQty = (selections[variantId] && selections[variantId].qty) || 0;
        if (inventoryLimit >= 0 && currentQty >= inventoryLimit) {
          showError('Only ' + inventoryLimit + ' left in stock for this item.');
          return;
        }
        if (!selections[variantId]) {
          selections[variantId] = { qty: 0, priceCents: priceCents, card: card };
          order.push(variantId);
        }
        selections[variantId].qty += 1;
        updateUI();
      });

      decreaseBtn.addEventListener('click', function () {
        clearError();
        if (!selections[variantId] || selections[variantId].qty === 0) return;
        selections[variantId].qty -= 1;
        if (selections[variantId].qty === 0) {
          delete selections[variantId];
          order = order.filter(function (id) { return id !== variantId; });
        }
        updateUI();
      });
    });

    submitBtn.addEventListener('click', function () {
      if (totalSelected() !== maxItems) return;
      clearError();

      var items;

      if (bundleVariantId) {
        // Bundle mode: add ONE line item (the real bundle product, at its real
        // fixed price) and attach the customer's picks as visible properties
        // so the merchant knows exactly what to pack. Real per-product prices
        // are never sent to cart in this mode.
        var properties = {};
        var counter = 1;
        order.forEach(function (variantId) {
          var entry = selections[variantId];
          if (!entry) return;
          var title = entry.card.dataset.title || variantId;
          properties['Pick ' + counter] = title + (entry.qty > 1 ? ' × ' + entry.qty : '');
          counter += 1;
        });
        items = [{ id: parseInt(bundleVariantId, 10), quantity: 1, properties: properties }];
      } else {
        // Fallback: add each real selected product/variant at its real price
        items = [];
        for (var id in selections) {
          items.push({ id: parseInt(id, 10), quantity: selections[id].qty });
        }
      }

      submitBtn.disabled = true;
      var originalText = submitBtn.querySelector('[data-bb-submit-text]').textContent;
      submitBtn.querySelector('[data-bb-submit-text]').textContent = 'Adding…';

      // Ask every cart-items-component on the page (e.g. the cart drawer) to
      // re-render itself from the sections returned by /cart/add.js — this is
      // the same "sections" trick product-form.js uses for the normal
      // add-to-cart buttons.
      var cartItemsComponents = document.querySelectorAll('cart-items-component');
      var sectionIds = [];
      cartItemsComponents.forEach(function (el) {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          sectionIds.push(el.dataset.sectionId);
        }
      });

      var deferredEventPromise = CartLinesUpdateEvent.createPromise();

      // Tell the theme (and any app, like Shiprocket/Fastrr, listening for
      // this same standard event) that items are being added — this is what
      // triggers the cart drawer to open instead of us hard-navigating away.
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'bundle-builder',
          lines: items.map(function (item) {
            return { merchandiseId: String(item.id), quantity: item.quantity };
          }),
          promise: deferredEventPromise.promise,
        })
      );

      var fetchCfg = fetchConfig('json', {
        body: JSON.stringify({ items: items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      });

      fetch(Theme.routes.cart_add_url, fetchCfg)
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              throw new Error(data.description || data.message || 'Something went wrong adding these items.');
            }
            return data;
          });
        })
        .then(function (data) {
          var addedItems = data.items;
          var addedSections = data.sections;

          return refreshCart().then(function (ajaxCart) {
            deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: {
                items: addedItems,
                source: 'bundle-builder',
                sections: addedSections,
                didError: false,
              },
            });

            // Belt-and-suspenders: directly open the cart drawer too, in case
            // something about our custom trigger point doesn't satisfy the
            // auto-open conditions the standard event normally relies on.
            var cartDrawerEl = document.getElementById('cart-drawer');
            if (cartDrawerEl && typeof cartDrawerEl.open === 'function' && !cartDrawerEl.isOpen) {
              cartDrawerEl.open();
            }

            // Reset the builder itself now that the picks live in the cart.
            selections = {};
            order = [];
            updateUI();
            submitBtn.querySelector('[data-bb-submit-text]').textContent = originalText;
          });
        })
        .catch(function (err) {
          deferredEventPromise.resolve({ cart: null, detail: { didError: true } });

          var message = err.message || '';
          var soldOutVariantId = findSoldOutVariantId(message);

          if (soldOutVariantId) {
            var title = selections[soldOutVariantId].card.dataset.title || 'That item';
            markSoldOut(soldOutVariantId);
            showError(title + ' just sold out — pick something else to complete your ' + maxItems + '.');
          } else {
            // Couldn't pin it to one product from the message alone — safest
            // move is to ask them to re-check their picks rather than let
            // them keep retrying the same failing combination.
            showError(message || 'One of your picks just sold out. Please review your selections and swap it for another.');
          }

          updateUI();
          submitBtn.querySelector('[data-bb-submit-text]').textContent = originalText;
        });
    });

    if (introPopup) {
      if (introClose) {
        introClose.addEventListener('click', function () {
          introPopup.hidden = true;
        });
      }
      introPopup.addEventListener('click', function (event) {
        if (event.target === introPopup) {
          introPopup.hidden = true;
        }
      });
    }

    if (introCta) {
      introCta.addEventListener('click', function () {
        if (introPopup) introPopup.hidden = true;
      });
    }

    updateUI();
  }

  function init() {
    var roots = document.querySelectorAll('[data-bundle-builder]');
    roots.forEach(initBundleBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-bundle-builder]');
    if (root) initBundleBuilder(root);
  });
})();